
import React, { useEffect, useState } from 'react';
import { Video, CheckCircle2, AlertTriangle, Loader2, Link2, LogOut } from 'lucide-react';
import {
  disconnectGoogleMeet,
  getGoogleMeetStatus,
  googleReturnMessage,
  startGoogleMeetConnect,
  type GoogleMeetStatus,
} from '../services/googleMeetApi';
import { describeError } from '../utils/describeError';

/**
 * Bloco #56: cada profissional conecta a própria conta Google e o Allodos cria
 * uma sala do Meet para cada teleconsulta. A sala abre pelo "Acessar Sala" do
 * agendamento, na Agenda.
 */
const Teleconsultation: React.FC = () => {
  const [status, setStatus] = useState<GoogleMeetStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(() => googleReturnMessage(window.location.search));

  const load = async () => {
    setLoadError(null);
    try {
      setStatus(await getGoogleMeetStatus());
    } catch (err) {
      setLoadError(describeError(err));
    }
  };

  useEffect(() => {
    void load();
    // Volta do Google: a mensagem já foi lida; limpa o endereço.
    if (new URLSearchParams(window.location.search).has('google')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await startGoogleMeetConnect();
      window.location.href = url;
    } catch (err) {
      setNotice({ ok: false, text: describeError(err) });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Desconectar a conta Google? As próximas teleconsultas deixam de ganhar sala própria no Meet (as salas já criadas continuam valendo).')) return;
    setBusy(true);
    try {
      await disconnectGoogleMeet();
      setNotice({ ok: true, text: 'Conta Google desconectada.' });
      await load();
    } catch (err) {
      setNotice({ ok: false, text: describeError(err) });
    } finally {
      setBusy(false);
    }
  };

  const revoked = status?.error === 'ACESSO_REVOGADO';

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-start gap-4 sm:gap-5">
          <div className="w-14 h-14 shrink-0 bg-emerald-100 dark:bg-emerald-950/40 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            <Video size={28} />
          </div>
          <div>
            <h2 className="text-2xl font-black dark:text-white">Sala no Google Meet</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed mt-1">
              Conecte sua conta Google e cada teleconsulta ganha uma sala exclusiva no Meet, criada automaticamente pelo Allodos. Você é o anfitrião da sala.
            </p>
          </div>
        </div>

        {notice && (
          <div className={`mt-6 p-4 rounded-2xl text-sm font-bold flex items-start gap-2 ${notice.ok ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300'}`}>
            {notice.ok ? <CheckCircle2 size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
            <span>{notice.text}</span>
          </div>
        )}

        <div className="mt-6 p-5 sm:p-6 rounded-3xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
          {!status && !loadError && (
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-400">
              <Loader2 size={16} className="animate-spin" /> Carregando...
            </p>
          )}
          {loadError && (
            <p className="text-sm font-bold text-rose-600">Não foi possível ver a conexão com o Google: {loadError}</p>
          )}
          {status && !status.configured && (
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 leading-relaxed">
              A integração com o Google Meet ainda não está ativa. Enquanto isso, a teleconsulta usa o link fixo da sala cadastrado em Configurações da Agenda › Locais de Atendimento.
            </p>
          )}
          {status?.configured && !status.connected && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Nenhuma conta Google conectada.</p>
                {/* A tela do Google traz a permissão do Meet desmarcada. */}
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">Na tela do Google, marque a permissão do Google Meet: ela vem desmarcada.</p>
              </div>
              <button
                type="button"
                onClick={() => void connect()}
                disabled={busy}
                className="px-5 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />} Conectar conta Google
              </button>
            </div>
          )}
          {status?.configured && status.connected && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2 break-all">
                  {revoked ? <AlertTriangle size={18} className="text-amber-600 shrink-0" /> : <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />}
                  <span>Conectado como <span className="font-black">{status.email || 'conta Google'}</span></span>
                </p>
                <div className="flex gap-2">
                  {revoked && (
                    <button type="button" onClick={() => void connect()} disabled={busy} className="px-4 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-black uppercase tracking-wider flex items-center gap-2">
                      <Link2 size={16} /> Reconectar
                    </button>
                  )}
                  <button type="button" onClick={() => void disconnect()} disabled={busy} className="px-4 py-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60 text-slate-600 dark:text-slate-300 text-xs font-black uppercase tracking-wider flex items-center gap-2">
                    <LogOut size={16} /> Desconectar
                  </button>
                </div>
              </div>
              {revoked && (
                <p className="text-xs font-bold text-amber-700 dark:text-amber-300">
                  O acesso do Allodos foi removido nesta conta Google. Reconecte para as próximas teleconsultas ganharem sala.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm">
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Como funciona</h3>
        <ol className="space-y-3 text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed list-decimal pl-5">
          <li>Cada agendamento online ganha a própria sala do Meet, criada na sua conta Google. O link fica nos detalhes do agendamento.</li>
          <li>Na Agenda, abra o atendimento e clique em <span className="font-black">Acessar Sala</span>: a sala abre em nova aba e o paciente recebe o link por WhatsApp e e-mail.</li>
          <li>Entre no Meet com a mesma conta Google conectada aqui: é ela que admite o paciente na sala.</li>
          <li>Sem conta conectada, a teleconsulta usa o link fixo do local online (Configurações da Agenda › Locais de Atendimento).</li>
        </ol>
      </div>
    </div>
  );
};

export default Teleconsultation;
